import React, { ReactNode, memo } from 'react'
import CarrierStatus from 'components/marketplace/CarrierStatus'
import SafetyStatus from 'components/marketplace/SafetyStatus'
import { Box } from 'transfix-ui/core'
import { SAFETY_RATING } from 'types/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ICarrierProps } from 'apps/marketplace/apiTypes/carriers'
import { TableRow, TableCell } from 'transfix-ui'
import formatCurrency from 'utils/formatCurrency'
import { formatDate } from 'utils/formatDatetime'
import { DISPLAY } from 'constants/display'
import HighwaySafetyStatus from '../Carrier/HighwaySafetyStatus'

interface ICarrierRowProps {
  carrier: ICarrierProps
}

const CarrierRow = memo<ICarrierRowProps>(({ carrier }) => {
  const { city, dotNumber, legalName, mcNumber, stateCode } = carrier || {}

  const companyDetailsCell = () => {
    return (
      <div>
        <a href={`${__ROOT__}/carriers/${carrier.id}`} target='_blank' rel='noopener noreferrer'>
          {carrier.id}
        </a>
        <p>{legalName}</p>
        <p>
          <b>MC:</b> {mcNumber}
        </p>
        <p>
          <b>DOT:</b> {dotNumber}
        </p>
      </div>
    )
  }

  const qualificationsCell = (): ReactNode[] => {
    const { qualifications = [] } = carrier || {}
    return qualifications.map(qualification => {
      return <div key={qualification.title}>{qualification.title}</div>
    })
  }

  const carrierManagersCell = (): ReactNode[] => {
    const { carrierManagers = [] } = carrier || {}
    return carrierManagers.map(carrierManager => {
      return (
        <div key={carrierManager.id}>
          {carrierManager.relationshipType}: {carrierManager.fullName}
        </div>
      )
    })
  }

  const getSafetyRating = (safetyRating?: string) => {
    switch (safetyRating) {
      case SAFETY_RATING.satisfactory:
        return SAFETY_RATING.satisfactory
      case SAFETY_RATING.conditional:
        return SAFETY_RATING.conditional
      case SAFETY_RATING.unsatisfactory:
        return SAFETY_RATING.unsatisfactory
      default:
        return SAFETY_RATING.notRated
    }
  }

  const safetyCell = () => {
    return (
      <>
        {carrier.fmcsaRating && (
          <div>
            <SafetyStatus status={getSafetyRating(carrier.fmcsaRating)} />
          </div>
        )}
        {carrier.insuredAmount && (
          <div>
            <b>Ins:</b> {formatCurrency(carrier.insuredAmount)}
          </div>
        )}
        {carrier.insuranceExpirationDate && (
          <div>
            <b>Exp:</b> {formatDate(carrier.insuranceExpirationDate, 'dateFormat')}
          </div>
        )}
      </>
    )
  }

  const isHighwayMilestoneFourComplianceTabEnabled = useFeatureFlag(
    'highwayMilestoneFourComplianceTab'
  )

  return (
    <TableRow>
      <TableCell>{companyDetailsCell()}</TableCell>
      <TableCell>
        {city}, {stateCode}
      </TableCell>
      <TableCell>{qualificationsCell()}</TableCell>
      <TableCell>{carrierManagersCell()}</TableCell>
      <TableCell>
        {!isHighwayMilestoneFourComplianceTabEnabled && safetyCell()}
        {isHighwayMilestoneFourComplianceTabEnabled && (
          <Box width='235px'>
            <HighwaySafetyStatus carrierId={`${carrier.id}`} />
          </Box>
        )}
      </TableCell>
      <TableCell>
        <a
          href={`${__ROOT__}/carriers/${carrier.id}/shipments`}
          target='_blank'
          rel='noopener noreferrer'>
          Total: {carrier.totalShipmentsCount}
        </a>
      </TableCell>
      <TableCell>{carrier.lastNote || DISPLAY.empty}</TableCell>
      <TableCell>
        {' '}
        <CarrierStatus displayStatus status={carrier.state} />
      </TableCell>
    </TableRow>
  )
})

export default CarrierRow
